/**
 * Dados MÍNIMOS obrigatórios do acompanhante do dia: nome completo, CPF e
 * telefone.
 *
 * Por que existe: o acompanhante entra na sala com o paciente e assina a
 * declaração de acompanhante — o documento precisa de nome e CPF, e o telefone
 * é o contato de quem esteve presente. Como o vínculo é feito com um PACIENTE
 * cadastrado, dava pra escapar por dois caminhos:
 *   1. cadastro rápido (PatientFormModal) exigia só o nome;
 *   2. seleção de um paciente antigo cujo cadastro já era incompleto.
 * Este módulo centraliza a regra pra os dois caminhos barrarem igual.
 */

import type { Patient } from '@/types/models';

export type AccompanistField = 'name' | 'cpf' | 'phone';

export const ACCOMPANIST_FIELD_LABEL: Record<AccompanistField, string> = {
  name: 'nome completo',
  cpf: 'CPF',
  phone: 'telefone',
};

/** Nome completo = pelo menos duas palavras (nome + sobrenome). */
export function isFullName(name: string | undefined | null): boolean {
  return (name || '')
    .trim()
    .split(/\s+/)
    .filter((part) => part.length >= 2).length >= 2;
}

/** Telefone brasileiro utilizável: 10 (fixo) ou 11 (celular) dígitos. */
export function isUsablePhone(phone: string | undefined | null): boolean {
  const digits = (phone || '').replace(/\D/g, '');
  return digits.length === 10 || digits.length === 11;
}

/**
 * O paciente tem CPF?
 *
 * Olha o `patientNumber` (guarda os 11 dígitos em claro quando há CPF) porque
 * `p.cpf` vem criptografado do Firestore — ver `patientCpfDisplay`. O fallback
 * pro campo `cpf` cobre o objeto recém-editado em memória, que já carrega o
 * CPF em claro antes do próximo fetch.
 */
export function patientHasCpf(p: Pick<Patient, 'patientNumber' | 'cpf'>): boolean {
  if (/^\d{11}$/.test(p.patientNumber || '')) return true;
  return !!(p.cpf || '').trim();
}

/** Campos obrigatórios que faltam pro paciente poder ser acompanhante. */
export function missingAccompanistData(
  p: Pick<Patient, 'name' | 'phone' | 'patientNumber' | 'cpf'>,
): AccompanistField[] {
  const missing: AccompanistField[] = [];
  if (!isFullName(p.name)) missing.push('name');
  if (!patientHasCpf(p)) missing.push('cpf');
  if (!isUsablePhone(p.phone)) missing.push('phone');
  return missing;
}

/** "CPF e telefone" / "nome completo, CPF e telefone" — pra mensagens de UI. */
export function formatMissingFields(fields: AccompanistField[]): string {
  const labels = fields.map((f) => ACCOMPANIST_FIELD_LABEL[f]);
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} e ${labels[labels.length - 1]}`;
}
